<template>
  <div class="ma-4">
    <v-toolbar class="mb-4">
      <v-toolbar-title>Banner Links</v-toolbar-title>
      <v-spacer />
      <v-btn v-if="isAdmin" to="/system/links" class="mr-2">Edit Links</v-btn>
      <v-text-field v-model="search" label="Search" outlined hide-details dense clearable style="max-width:300px">
        <template v-slot:prepend-inner>
          <v-icon small style="margin-top:4px">fal fa-search</v-icon>
        </template>
      </v-text-field>
    </v-toolbar>
    <v-tabs v-model="tab">
      <v-tab v-for="{ category, label, linkCount } of filtered" :key="'tab-' + category">
        <v-badge v-if="search != null && search !== ''" :content="linkCount || '0'">{{ label }}</v-badge>
        <span v-else>{{ label }}</span>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item v-for="{ category, main, groups } of filtered" :key="'content-' + category" class="pa-4" :style="'background-color:#' + (darkMode ? '333' : 'EEE')">
        <template v-if="main.length > 0">
          <link-row :links="main" class="mb-4" />
        </template>
        <v-expansion-panels v-if="groups.length > 0" v-model="subgroup">
          <v-expansion-panel v-for="{ group, links } of groups" :key="`${category}-${group}`">
            <v-expansion-panel-header>{{ group }}</v-expansion-panel-header>
            <v-expansion-panel-content :style="'background-color:#' + (darkMode ? '333' : 'EEE')">
              <link-row :links="links" class="mt-4" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api'

export default {
  components: {
    LinkRow: () => import('@/components/system/linkRow')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const isAdmin = computed(() => ['jon.moon', 'crocker', 'lynch'].includes(root.$store.state.user.username))
    const darkMode = computed(() => user.value.settings && 'darkMode' in user.value.settings && user.value.settings.darkMode)
    const loading = ref(true)
    const tab = ref(0)
    const subgroup = ref(null)
    const categories = ref([])

    const search = ref('')
    function checkText (text) {
      const temp = search.value.split(' ')
      for (const word of temp) {
        const regex = new RegExp(word, 'ig')
        if (!regex.test(text)) return false
      }
      return true
    }
    const filtered = computed(() => {
      if (search.value === '' || search.value == null) return categories.value
      return categories.value.map(({ category, label, main: m, groups: g }) => {
        const main = m.filter(({ title, subtitle, link }) => checkText(`${title} ${subtitle} ${link}`))
        const groups = g.map(({ group, links: l }) => {
          if (checkText(group)) return { group, links: l }
          const links = l.filter(({ title, subtitle, link }) => checkText(`${title} ${subtitle} ${link}`))
          return { group, links }
        }).filter(({ links }) => links.length > 0)
        return {
          category,
          label,
          main,
          groups,
          linkCount: (main.length + groups.reduce((prev, { links }) => prev + links.length, 0)) || 0
        }
      })
    })
    watch(filtered, () => {
      if (search.value !== '') {
        if (filtered.value[tab.value].main.length === 0 && filtered.value[tab.value].groups.length === 0) {
          for (let i = tab.value; i < filtered.value.length; i++) {
            if (filtered.value[i].main.length > 0 || filtered.value[i].groups.length > 0) {
              tab.value = i
              return
            }
          }
          for (let i = 0; i < tab.value; i++) {
            if (filtered.value[i].main.length > 0 || filtered.value[i].groups.length > 0) {
              tab.value = i
              return
            }
          }
        }
      }
    })

    onMounted(async () => {
      const { total: courseTotal } = await root.$feathers.service('calendar/classes').find({ query: { 'meetingBase.startDate': { $lte: new Date() }, 'meetingBase.endDate': { $gte: new Date() }, 'instructors.pidm': user.value.pidm, $limit: 0 } })
      const aggregate = [
        { $match: { category: { $regex: '^Links Page - ' } } },
        { $unwind: '$groups' },
        { $lookup: {
          from: 'System-Links',
          localField: 'groups.links',
          foreignField: '_id',
          as: 'links'
        } },
        { $project: {
          category: 1,
          group: '$groups.name',
          links: 1,
          linkCount: { $size: '$links' }
        } },
        { $sort: { category: 1, group: 1 } }
      ]
      const { data } = await root.$feathers.service('system/link-category').find({ query: { aggregate } })
      const roles = [ ...user.value.roles ]
      if (user.value.roles.includes('Student')) {
        const year = new Date().getFullYear()
        const month = new Date().getMonth()
        let aidy = ((year % 100) * 101) + 1
        if (month < 5) aidy -= 101
        else if (month < 7) aidy = ''
        aidy += ''
        const { total } = await root.$feathers.service('work-study/students').find({ query: { pidm: user.value.pidm, aidy, $limit: 0 } })
        if (total > 0) roles.push('ScotsWork')
      }
      for (const { category, group, links } of data) {
        if (links.length === 0) continue
        const index = categories.value.findIndex(({ category: c }) => c === category)
        if (index >= 0) {
          if (group === 'Main') categories.value[index].main = links
          else categories.value[index].groups.push({ group, links })
        } else {
          const label = category.substring(13)
          // Check for permission to the section before adding it
          // Employee section: All Employees and ScotsWork Workers
          if (roles.includes(label) ||
              roles.includes('Technology Services') || roles.includes('Records') ||
              (label === 'Faculty' && (roles.includes('All Faculty') || courseTotal > 0)) ||
              (label === 'Employee' && roles.includes('ScotsWork')) ||
              (label === 'Finance' && roles.includes('Employee')) ||
              (label === 'Financial Aid' && roles.includes('Student'))) {
            const obj = { category, label, main: [], groups: [] }
            if (group === 'Main') obj.main = links
            else obj.groups.push({ group, links })
            categories.value.push(obj)
          }
        }
      }
      if (root.$route.hash !== '') {
        const temp = root.$route.hash.substring(1).toLowerCase().split('-')
        const index = categories.value.findIndex(({ label }) => label.toLowerCase().replaceAll(' ', '_') === temp[0])
        if (index >= 0) {
          tab.value = index
          if (temp.length > 1 && categories.value[index].groups.length > 0) {
            const subIndex = categories.value[index].groups.findIndex(({ group }) => group.toLowerCase().replaceAll(' ', '_') === temp[1])
            console.log('Sub Index found: ' + subIndex)
            if (subIndex >= 0) {
              subgroup.value = subIndex
            }
          }
        } else {
          tab.value = 0
        }
      }
      loading.value = false
    })

    return {
      user,
      isAdmin,
      darkMode,
      loading,
      tab,
      subgroup,
      categories,
      search,
      filtered
    }
  }
}
</script>
